// ADR 24.9.26l §2 — a route change moves focus into the content.
//
// A single-page app's navigation is silent: the old page's link unmounts, focus
// falls to `<body>`, and the next Tab starts again at the top of the chrome.
// So on every `pathname` change after the shell's first commit, focus goes to
// `#main` — the same place the skip link lands (15.9.26ah AC6), which already
// carries `tabIndex={-1}`. Not a heading: the only `<h1>` is the header's
// destination title, and signed-out pages have none (Context finding 1).
//
// `preventScroll`, so `ScrollRestoration` keeps sole ownership of scroll.
// Search- and hash-only changes move nothing: the dependency is the pathname.

import { useEffect, useRef } from 'react'

/**
 * Focus `#main` unless the reader's focus is still connected inside it — at
 * `list_detail` the shell-mounted plan list survives `/plans/7` → `/plans/8`,
 * and sending focus to the top of `#main` would lose their place in the list.
 */
function focusMainUnlessKept(doc: Document = document): void {
  const main = doc.getElementById('main')
  if (main === null) return
  const active = doc.activeElement
  if (active !== null && active !== doc.body && active.isConnected && main.contains(active)) return
  main.focus({ preventScroll: true })
}

/**
 * Run the focus move, then `onArrive`, on every pathname change after the
 * first commit. Keyed on the last pathname seen rather than a "first run"
 * flag, so StrictMode's double effect in development is not a navigation.
 */
export function useRouteFocus(pathname: string, onArrive: () => void): void {
  const last = useRef(pathname)
  useEffect(() => {
    if (last.current === pathname) return
    last.current = pathname
    focusMainUnlessKept()
    onArrive()
  }, [pathname, onArrive])
}
